import React from 'react';
import { motion } from 'framer-motion';
import { Mail, Sparkles, ShieldCheck, ArrowRight, Check } from 'lucide-react';

const FIELDS = [
  { label: 'Trade date',    value: '20 Apr 2026',         w: 0.55, confidence: 'high' },
  { label: 'Counterparty',  value: 'Vitol SA',            w: 0.7,  confidence: 'high' },
  { label: 'Commodity',     value: 'Murban Crude',        w: 0.8,  confidence: 'high' },
  { label: 'Volume',        value: '500,000 bbl',         w: 0.6,  confidence: 'high' },
  { label: 'Price',         value: 'USD 78.45 / bbl',     w: 0.75, confidence: 'high' },
  { label: 'Loading port',  value: 'Ras Tanura',          w: 0.65, confidence: 'review' },
  { label: 'Laycan',        value: '10–12 May 2026',      w: 0.7,  confidence: 'high' },
  { label: 'Incoterms',     value: 'FOB',                 w: 0.4,  confidence: 'high' },
];

const Field = ({ label, value, confidence, delay }) => (
  <motion.div
    initial={{ opacity: 0, y: 8 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, amount: 0.3 }}
    transition={{ duration: 0.5, delay, ease: [0.16, 1, 0.3, 1] }}
    className="flex items-center justify-between py-2.5 border-b border-white/[0.05] last:border-0"
  >
    <div className="flex items-center gap-2">
      <span className="text-[10.5px] font-medium tracking-[0.06em] uppercase text-ink-faint/80">
        {label}
      </span>
    </div>
    <div className="flex items-center gap-2">
      <span className="mono-tabular text-[12.5px] text-canvas-white">{value}</span>
      {confidence === 'high' ? (
        <span className="w-3.5 h-3.5 rounded-full bg-success/20 ring-1 ring-success/40 flex items-center justify-center">
          <Check size={8} strokeWidth={3} className="text-success" />
        </span>
      ) : (
        <span className="text-[9.5px] font-semibold tracking-wide uppercase px-1.5 py-0.5 rounded-sm bg-amber/15 text-amber-light ring-1 ring-amber/30">
          review
        </span>
      )}
    </div>
  </motion.div>
);

const TradeTicketVisual = () => {
  return (
    <div className="relative w-full mx-auto max-w-5xl">
      {/* ambient glow */}
      <div className="absolute -inset-x-20 -top-10 h-[420px] bg-radial-amber pointer-events-none" />
      <div
        className="absolute -inset-px rounded-[24px] bg-gradient-to-b from-white/[0.08] to-transparent"
        aria-hidden="true"
      />
      <div className="relative rounded-[22px] border border-white/[0.08] bg-canvas-surface/70 backdrop-blur-xl shadow-card-dark overflow-hidden">
        {/* window chrome */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-white/[0.06] bg-white/[0.015]">
          <div className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-full bg-white/15" />
            <span className="w-2.5 h-2.5 rounded-full bg-white/15" />
            <span className="w-2.5 h-2.5 rounded-full bg-white/15" />
          </div>
          <div className="text-[11px] font-mono text-ink-faint/70">coretrade.ai / inbox · trade-confirmations</div>
          <div className="flex items-center gap-1.5">
            <span className="w-1.5 h-1.5 rounded-full bg-success animate-pulse-soft" />
            <span className="text-[10.5px] font-medium uppercase tracking-wider text-ink-faint/80">live</span>
          </div>
        </div>

        <div className="grid md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-white/[0.06]">
          {/* LEFT: email thread */}
          <div className="p-6 md:p-7">
            <div className="flex items-center gap-2 mb-5">
              <div className="w-7 h-7 rounded-full bg-white/[0.06] flex items-center justify-center">
                <Mail size={13} className="text-ink-faint" />
              </div>
              <div>
                <p className="text-[11px] font-medium uppercase tracking-[0.08em] text-ink-faint">
                  Source
                </p>
                <p className="text-[12.5px] text-canvas-white">Email thread · 7 messages</p>
              </div>
            </div>

            <div className="space-y-2.5 text-[12px] leading-relaxed text-ink-faint/90 font-mono">
              <p>
                <span className="text-ink-faint/60">From:</span> trader@vitol.com
              </p>
              <p className="text-canvas-white/90">
                Confirming{' '}
                <span className="bg-amber/15 text-amber-light px-1 rounded-sm">500kb Murban</span>{' '}
                @ <span className="bg-amber/15 text-amber-light px-1 rounded-sm">78.45</span> FOB
                <span className="bg-amber/15 text-amber-light px-1 rounded-sm"> Ras Tanura</span>,
                laycan <span className="bg-amber/15 text-amber-light px-1 rounded-sm">10-12 May</span>.
                Pls confirm.
              </p>
              <p className="text-ink-faint/50">— — —</p>
              <p>
                <span className="text-ink-faint/60">From:</span> me
              </p>
              <p className="text-canvas-white/90">
                Confirmed. Booking now.
              </p>
            </div>

            <div className="mt-7 pt-5 border-t border-white/[0.06] flex items-center gap-2">
              <Sparkles size={13} className="text-amber" />
              <p className="text-[11.5px] text-ink-faint">
                Extracted{' '}
                <span className="text-canvas-white font-medium">12 fields</span>
                {' '}in 1.4s
              </p>
            </div>
          </div>

          {/* RIGHT: structured ticket */}
          <div className="p-6 md:p-7 bg-gradient-to-br from-white/[0.015] to-transparent">
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-full bg-amber/15 ring-1 ring-amber/25 flex items-center justify-center">
                  <Sparkles size={13} className="text-amber" />
                </div>
                <div>
                  <p className="text-[11px] font-medium uppercase tracking-[0.08em] text-amber-light">
                    Drafted ticket
                  </p>
                  <p className="text-[12.5px] text-canvas-white font-mono">TRD-2026-04-20-001</p>
                </div>
              </div>
              <span className="text-[10.5px] font-mono text-ink-faint">11/12 verified</span>
            </div>

            <div>
              {FIELDS.map((f, i) => (
                <Field key={f.label} {...f} delay={0.05 + i * 0.045} />
              ))}
            </div>

            <div className="mt-6 pt-5 border-t border-white/[0.06] flex items-center gap-2">
              <button className="inline-flex items-center gap-1.5 text-[12px] font-medium px-3.5 py-1.5 rounded-full bg-amber text-canvas-black hover:bg-amber-light transition-colors">
                Approve & post
                <ArrowRight size={12} />
              </button>
              <button className="text-[12px] text-ink-faint hover:text-canvas-white px-2 py-1.5">
                Edit
              </button>
              <span className="ml-auto inline-flex items-center gap-1.5 text-[10.5px] font-medium text-success">
                <ShieldCheck size={12} />
                Audit-logged
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TradeTicketVisual;
